import { useDebugModeStore } from '../../components/Design/state/debugModeStore';
import { formatCanvasNodesPositions } from '../../components/FlowCanvas/utils';
import { useCanvasStore, LevelPathItem } from '../canvasStore';
import { useComponentConfigStore } from '../componentConfigStore';
import { useTrackStore } from '../trackStore';

export interface ChangeLevelParams {
  levelType: 'start' | 'onProcess' | 'onException';
  componentId?: string;
}

/**
 * Helper to update currentLevelName and currentLevelPath in canvasStore
 *
 */
export function changeLevel({
  levelType,
  componentId
}: ChangeLevelParams): void {
  const canvasStore = useCanvasStore.getState();
  const trackStore = useTrackStore.getState();
  const componentConfigStore = useComponentConfigStore.getState();
  const { isDebugMode } = useDebugModeStore.getState();
  let levelName;

  if (levelType === 'start') {
    levelName = 'start';
    const startLevelPath: LevelPathItem[] = [
      {
        label: 'root',
        type: 'start'
      }
    ];

    if (isDebugMode) {
      useDebugModeStore
        .getState()
        .setDebuggerCurrentLevel(startLevelPath, levelName);
    }

    canvasStore.setCurrentLevel(startLevelPath, levelName);
  }

  if (levelType !== 'start') {
    if (!componentId) {
      console.warn('componentId is required for non-start levels');
      return;
    }

    levelName = `${componentId}-${levelType}Track`;
    const levelPath = buildLevelPath(
      levelName,
      trackStore,
      componentConfigStore
    );
    if (isDebugMode) {
      useDebugModeStore
        .getState()
        .setDebuggerCurrentLevel(levelPath, levelName);
    }
    canvasStore.setCurrentLevel(levelPath, levelName);

    if (
      levelName.endsWith('onProcessTrack') ||
      levelName.endsWith('onExceptionTrack')
    ) {
      const { nodes } = canvasStore;
      const startTrackExists = nodes.has('start-track');

      if (!startTrackExists) {
        canvasStore.addNode({
          id: 'start-track',
          position: { x: 0, y: 0 },
          tags: ['start-track'],
          deletable: false
        });
      }
    }
  }

  const formattedResult = formatCanvasNodesPositions({
    nodes: canvasStore.nodes,
    edges: canvasStore.edges,
    currentLevelName: levelName
  });

  const nodePositions = Array.from(formattedResult.nodes.entries()).map(
    ([nodeId, node]) => ({
      nodeId,
      position: node.position
    })
  );

  canvasStore.updateNodesPositions(nodePositions);
}

/**
 * Build level path by traversing parent components
 */
function buildLevelPath(
  trackName: string,
  trackStore: ReturnType<typeof useTrackStore.getState>,
  componentConfigStore: ReturnType<typeof useComponentConfigStore.getState>,
  acc: LevelPathItem[] = []
): LevelPathItem[] {
  if (trackName === 'start') {
    if (acc.length === 0) {
      return [{ label: 'root', type: 'start' }];
    }
    return acc;
  }

  const track = trackStore.getTrack(trackName);
  if (!track) {
    console.warn(`Track not found: ${trackName}`);
    return acc.length > 0 ? acc : [{ label: 'root', type: 'start' }];
  }

  const { parentComponentId } = track;
  if (!parentComponentId) {
    return acc;
  }

  const parentConfig = componentConfigStore.getConfig(parentComponentId);
  if (!parentConfig) {
    console.warn(`Parent component config not found: ${parentComponentId}`);
    return acc;
  }

  const currentLevelType = track.metadata?.levelType || track.type;
  const isLevelTrack =
    currentLevelType === 'onProcess' || currentLevelType === 'onException';

  const label = `${parentConfig.stepName} (${currentLevelType})`;
  const currentLevelPathItem: LevelPathItem = {
    label,
    type: currentLevelType as 'start' | 'onProcess' | 'onException',
    parent: parentComponentId
  };

  const path = isLevelTrack ? [currentLevelPathItem, ...acc] : acc;

  const parentTrack = trackStore.getTrackByComponent(parentComponentId);
  if (!parentTrack) {
    return path;
  }

  return buildLevelPath(
    parentTrack.name,
    trackStore,
    componentConfigStore,
    path
  );
}
